import React from 'react';
import Page from '../../common/components/page/Page';
import { PageTemplates } from '../../common/components/page/PageTemplates';
import {
  IdentityDeniedAlert,
  IdentityNotUploadedAlert,
  MailingAddressNotCompleteAlert,
} from '../apply-for-registration/NotAllowedAlerts';
import ApplicationDetail from '../../domain/types/ApplicationDetail';
import DisplayAddress from 'domain/components/address/DisplayAddress';
import {
  Button,
  Col,
  FormFile,
  FormGroup,
  FormLabel,
  FormText,
  Row,
} from 'react-bootstrap';
import ChsProfile from 'domain/types/ChsProfile';
import arrayMutators from 'final-form-arrays';
import ActionStatus from 'common/types/ActionStatus';
import useCodeTable from 'common/hooks/useCodeTable';
import Alert from 'common/components/alert/Alert';
import AlertType from 'common/types/AlertType';
import Modal from 'common/components/modal/Modal';
import ClientFacingStatus from '../../domain/enums/ClientFacingStatus';
import AcceptableDocsLink from 'common/components/links/AcceptableDocsLink';
import { FieldArray } from 'react-final-form-arrays';
import IntermediariesList from 'common/components/intermediaries-list/IntermediaryList';
import { Form, FormSpy } from 'react-final-form';
import FinalFormField from 'common/components/final-form/FinalFormField';
import { RenewalFormData } from './types';
import DwellingTypes from 'domain/enums/DwellingTypes';
import { CodeTableEntry } from 'domain/components/codetable/CodeTableOptions';

interface FormValidationMessages {
  intermediaries?: string;
  file1Uploaded?: string;
  file2Uploaded?: string;
  howManyRooms?: string;
  attachmentType1Cd?: string;
  attachmentType2Cd?: string;
  primaryResIndicator?: string;
  propertyRelaCd?: string;
}

interface IRenewRegistrationPresenter {
  applicationDetails: ApplicationDetail;
  profile: ChsProfile;
  identity: {
    rejected: boolean;
    uploaded: boolean;
    loading: boolean;
  };
  allowedFileTypes?: string | null;
  isDocument1Uploaded: boolean;
  isDocument2Uploaded: boolean;
  error?: string;
  submitStatus: ActionStatus;
  submitMessage: string;
  onReturnToDashboardClick: () => void;
  onAttachment1Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAttachment2Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (values: RenewalFormData) => Promise<void>;
}

interface UploadFileProps {
  id: string;
  text: string;
  submitStatus: ActionStatus;
  errorKey: string;
  onAttachment1Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  select: {
    name: string;
    label: string;
    options: CodeTableEntry[];
  };
}

const UploadFile = ({
  id,
  text,
  submitStatus,
  errorKey,
  select,
  onAttachment1Change,
}: UploadFileProps) => {
  const { name, label, options } = select;
  return (
    <FormGroup as={Row}>
      <Col sm={{ span: 6 }} className='align-self-center'>
        <FormLabel>{text}</FormLabel>
        <FormFile
          type='file'
          id={id}
          onChange={onAttachment1Change}
          disabled={submitStatus === ActionStatus.saving}
        />
        <FormSpy>
          {({ errors }) => (
            <>
              {errors && errors[errorKey] && (
                <div className={`d-block invalid-feedback`}>
                  {errors[errorKey]}
                </div>
              )}
            </>
          )}
        </FormSpy>
        <FinalFormField
          type='select'
          name={name}
          label={label}
          options={options}
          disabled={submitStatus === ActionStatus.saving}
        />
        <FormText className='text-muted'>
          All documentation must be current/unexpired.
        </FormText>
      </Col>
    </FormGroup>
  );
};

const RenewRegistrationPresenter = (props: IRenewRegistrationPresenter) => {
  const {
    applicationDetails,
    submitStatus,
    isDocument1Uploaded,
    isDocument2Uploaded,
    onSubmit,
    onAttachment1Change,
    onAttachment2Change,
  } = props;

  const intermediaryCodes = useCodeTable({ endpoint: '/intermediaries' });
  const dwellingCodes = useCodeTable({ endpoint: '/dwellingTypes' });
  const relationshipTypes = useCodeTable({
    endpoint: '/propertyRelationshipTypes',
  });
  const options = useCodeTable({
    includeEmpty: true,
    endpoint: '/primaryResidencyAttachments',
  }).filter((c) => c.cd !== 'LICENSE_OR_ID');

  const options2 = useCodeTable({
    includeEmpty: true,
    endpoint: '/primaryResidencyAttachments',
  }).filter((c) => c.cd === 'LICENSE_OR_ID' || c.cd === '');
  const disableSubmit = props.submitStatus === ActionStatus.saving;

  const mappedIntermediaryCodes = intermediaryCodes.map((opt) => ({
    value: opt.cd,
    label: opt.descr,
  }));

  const restrictionAlerts: JSX.Element[] = [];

  function addRestrictionAlert(el: JSX.Element) {
    restrictionAlerts.push(el);
  }

  if (!props.identity.uploaded) {
    addRestrictionAlert(<IdentityNotUploadedAlert />);
  }
  if (props.identity.rejected) {
    addRestrictionAlert(<IdentityDeniedAlert />);
  }
  if (!props.profile.address) {
    addRestrictionAlert(<MailingAddressNotCompleteAlert />);
  }

  // If there are any restrictions, show them and disallow user from renewing
  if (restrictionAlerts.length > 0) {
    return <>{restrictionAlerts.map((a) => a)}</>;
  }

  if (
    applicationDetails.clientFacingStatusCd !==
    ClientFacingStatus.READY_FOR_RENEWAL
  ) {
    return <p>This application is not able to be renewed.</p>;
  }

  return (
    <Page template={PageTemplates.fullWidth}>
      <Modal
        show={props.submitStatus === ActionStatus.saved}
        title='Success'
        body={<p>Your request for renewal has been successfully submitted. </p>}
        footer={
          <>
            <Button
              variant='outline-primary'
              onClick={props.onReturnToDashboardClick}
            >
              Return to Dashboard
            </Button>
          </>
        }
      />
      <Form<RenewalFormData>
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validate={(values) => {
          const errors: FormValidationMessages = {
            intermediaries: undefined,
            file1Uploaded: undefined,
            file2Uploaded: undefined,
            howManyRooms: undefined,
            attachmentType1Cd: undefined,
            attachmentType2Cd: undefined,
            primaryResIndicator: undefined,
            propertyRelaCd: undefined,
          };

          if (
            !values.intermediaries ||
            (values.intermediaries &&
              values.intermediaries.filter((i) => Boolean(i)).length === 0)
          ) {
            errors.intermediaries =
              'You must select at least one intermediary.';
          }

          if (values.propertyRelaCd === 'NONE') {
            errors.propertyRelaCd =
              'Please select your relationship with the property.';
          } else if (
            values.propertyRelaCd === 'OWNER' ||
            values.propertyRelaCd === 'TENANT'
          ) {
            const isPrimaryResidence = values.primaryResIndicator === 'Y';

            if (values.primaryResIndicator === 'NONE') {
              errors.primaryResIndicator = 'Please select.';
            } else {
              if (isPrimaryResidence) {
                if (!isDocument1Uploaded) {
                  errors.file1Uploaded =
                    "Primary Residency: ID or Driver's License";
                }
                if (!isDocument2Uploaded) {
                  errors.file2Uploaded =
                    'Primary Residency: Supplementary Document';
                }

                if (!values.attachmentType1Cd) {
                  errors.attachmentType1Cd = 'Please select document type.';
                }

                if (!values.attachmentType2Cd) {
                  errors.attachmentType2Cd = 'Please select document type.';
                }

                if (
                  values.attachmentType1Cd &&
                  values.attachmentType2Cd &&
                  values.attachmentType1Cd === values.attachmentType2Cd
                ) {
                  errors.attachmentType2Cd =
                    'You must select different document types.';
                }
              }
            }
          }

          if (
            (values.dwellingTypeCd === DwellingTypes.COMBINATION ||
              values.dwellingTypeCd === DwellingTypes.PARTIAL) &&
            !values.howManyRooms
          ) {
            errors.howManyRooms =
              'You must enter the number of rooms you are listing.';
          }

          return errors;
        }}
        mutators={{ ...arrayMutators }}
        initialValues={{
          dwellingTypeCd: applicationDetails.dwellingTypeCd || '',
          howManyRooms: applicationDetails.howManyRooms,
          intermediaries: applicationDetails.intermediaries,
          propertyRelaCd: applicationDetails.propertyRelationshipCd || '',
          primaryResIndicator: applicationDetails.primaryResIndicator || 'N',
        }}
      >
        {({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            <h2 className='mb-5'>Short term rental registration renewal</h2>
            <p>
              Your profile details will be used to request renewal for this
              registration.{' '}
            </p>
            <p>
              <strong>Profile Name:</strong>{' '}
              {`${props.profile.firstName} ${props.profile.lastName}${
                props.profile.suffix ? ` ${props.profile.suffix}` : ''
              }`}
            </p>
            <p>
              <strong>Email:</strong> {props.profile.email}
            </p>
            <FormGroup as={Row}>
              <Col sm={{ span: 6 }} className='align-self-center'>
                <FormLabel>Intermediares</FormLabel>
                <FieldArray name='intermediaries'>
                  {({ fields, fields: { value }, meta }) => {
                    return (
                      <>
                        <IntermediariesList
                          options={mappedIntermediaryCodes}
                          selectedValues={value}
                          onSelect={(selectedValue) => {
                            const opt = mappedIntermediaryCodes.find(
                              (c) => c.value === selectedValue
                            );
                            fields.push({
                              cd: opt?.value,
                              descr: opt?.label,
                            });
                          }}
                          onRemove={(selectedValue) => {
                            const removeIdx = value.findIndex(
                              (c) => c.cd === selectedValue
                            );
                            if (removeIdx !== -1) {
                              fields.remove(removeIdx);
                            } else {
                              console.error(
                                'Item cannot be removed. Index not found.'
                              );
                            }
                          }}
                        />
                        {meta.error && (
                          <div className={`d-block invalid-feedback`}>
                            {meta.error}
                          </div>
                        )}
                      </>
                    );
                  }}
                </FieldArray>
              </Col>
            </FormGroup>
            <FormGroup as={Row}>
              <Col sm={{ span: 6 }} className='align-self-center'>
                <FinalFormField
                  name='dwellingTypeCd'
                  label='Room type'
                  type='select'
                  options={dwellingCodes.filter((d) => d.cd !== 'NONE')}
                  when={{
                    field: 'dwellingTypeCd',
                    becomes: DwellingTypes.ENTIRE,
                    set: 'howManyRooms',
                    to: null,
                  }}
                />
              </Col>
            </FormGroup>
            {(values.dwellingTypeCd === DwellingTypes.COMBINATION ||
              values.dwellingTypeCd === DwellingTypes.PARTIAL) && (
              <FormGroup as={Row}>
                <Col sm={{ span: 6 }} className='align-self-center'>
                  <FinalFormField
                    type='number'
                    name='howManyRooms'
                    label='Total rooms'
                  />
                </Col>
              </FormGroup>
            )}
            <FormGroup as={Row}>
              <Col sm={{ span: 6 }} className='align-self-center'>
                <FormLabel>
                  What is your relationship to the property: *
                </FormLabel>
                <FinalFormField
                  type='select'
                  name='propertyRelaCd'
                  label={null}
                  options={relationshipTypes}
                />
              </Col>
            </FormGroup>

            {(values.propertyRelaCd === 'OWNER' ||
              values.propertyRelaCd === 'PRO_HOST' ||
              values.propertyRelaCd === 'TENANT') && (
              <>
                <p>
                  <strong>
                    Do you live in this unit as your primary residence?
                  </strong>
                </p>
                <p>{values.primaryResIndicator === 'Y' ? 'Yes' : 'No'}</p>
                {values.primaryResIndicator === 'Y' && (
                  <>
                    {props.submitStatus === ActionStatus.failed && (
                      <Alert type={AlertType.danger}>
                        {props.submitMessage}
                      </Alert>
                    )}
                    <p>
                      Please upload proof of residence for the following
                      address:
                    </p>
                    <p>
                      <DisplayAddress
                        address={props.applicationDetails.address}
                      />
                    </p>
                    <FormGroup as={Row}>
                      <Col sm={{ span: 6 }} className='align-self-center'>
                        <FormLabel htmlFor='files'>
                          Please upload <u>2 documents</u> from different
                          categories on the{' '}
                          <AcceptableDocsLink>approved list</AcceptableDocsLink>{' '}
                          showing proof of primary residency. *
                        </FormLabel>
                        <FormText className='text-muted'>
                          Allowed File Types: {props.allowedFileTypes}
                        </FormText>
                      </Col>
                    </FormGroup>
                    <UploadFile
                      id='file-1'
                      errorKey='file1Uploaded'
                      text="Primary Residency: ID or Driver's License"
                      submitStatus={submitStatus}
                      onAttachment1Change={onAttachment1Change}
                      select={{
                        name: 'attachmentType1Cd',
                        label: 'Document type *',
                        options: options2,
                      }}
                    />
                    <UploadFile
                      id='file-2'
                      text='Primary Residency: Supplementary Document'
                      errorKey='file2Uploaded'
                      submitStatus={submitStatus}
                      onAttachment1Change={onAttachment2Change}
                      select={{
                        name: 'attachmentType2Cd',
                        label: 'Document type *',
                        options,
                      }}
                    />
                  </>
                )}
              </>
            )}
            <div className='d-flex'>
              <Button
                variant='primary'
                type='submit'
                className='ml-auto'
                disabled={disableSubmit}
              >
                Submit Request for Renewal
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Page>
  );
};

export default RenewRegistrationPresenter;
